import React, { useCallback, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { Query, QueryKey, useQueryClient } from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { RequestException } from '@eika-infrastruktur/request/src/exceptions';
import { ButtonWithSpinner } from '@eika/button-with-spinner';

import useTranslation from '../../hooks/useTranslation';
import { globalCssNamespace } from '../../index';
import { logErrorFromErrorBoundary } from '../../utils/logging/logError';
import { ErrorCardWrapper } from '../shared/ErrorCardWrapper';

import './ErrorCatcher.scss';

export type FallbackErrorCatcherProps = {
    error: RequestExceptionWithResponse;
    resetErrorBoundary: () => void;
};

type FallbackErrorCardProps = {
    queriesToInvalidateOnRetry: QueryKey[];
    noBackgroundOnErrorCard: boolean;
    showErrorCardOnBackendError?: boolean;
    showErrorCard?: boolean;
} & FallbackErrorCatcherProps;

export const FallbackErrorCatcher: React.FC<FallbackErrorCardProps> = ({
    error,
    resetErrorBoundary,
    queriesToInvalidateOnRetry,
    noBackgroundOnErrorCard,
    showErrorCardOnBackendError = true,
    showErrorCard = true,
}) => {
    const translated = useTranslation();
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);

    const predicate = useCallback(
        (query: Query): boolean =>
            queriesToInvalidateOnRetry.some((keyToInvalidate) => {
                return keyToInvalidate[0] === query.queryKey[0];
            }),
        [queriesToInvalidateOnRetry],
    );

    const tryAgain = useCallback(async () => {
        setIsLoading(true);
        if (queriesToInvalidateOnRetry.length > 0) await queryClient.refetchQueries({ predicate });
        resetErrorBoundary();
        setIsLoading(false);
    }, [queryClient, predicate, resetErrorBoundary, queriesToInvalidateOnRetry]);

    if (!showErrorCard) {
        return null;
    }

    if (
        (error.name.includes('RequestExceptionWithResponse') || error instanceof RequestException) &&
        !showErrorCardOnBackendError
    ) {
        return null;
    }

    return (
        <div className={globalCssNamespace}>
            <ErrorCardWrapper className="error-catcher-wrapper" exception={error} isModal={noBackgroundOnErrorCard}>
                <ButtonWithSpinner showSpinner={isLoading} onClick={tryAgain}>
                    {translated.tryAgain}
                </ButtonWithSpinner>
            </ErrorCardWrapper>
        </div>
    );
};

type ErrorCatcherProps = {
    appNamespace: string;
    children: React.ReactNode;
    queriesToInvalidateOnRetry?: QueryKey[];
    noBackgroundOnErrorCard?: boolean;
    showErrorCardOnBackendError?: boolean;
    showErrorCard?: boolean;
    fallbackComponent?: React.ComponentType<FallbackErrorCatcherProps>;
};

const ErrorCatcher: React.FC<ErrorCatcherProps> = ({
    queriesToInvalidateOnRetry = [],
    appNamespace,
    noBackgroundOnErrorCard = false,
    showErrorCardOnBackendError = true,
    showErrorCard = true,
    fallbackComponent,
    children,
}) => {
    const FallbackErrorCard = useCallback(
        (props: FallbackErrorCatcherProps) => (
            <FallbackErrorCatcher
                queriesToInvalidateOnRetry={queriesToInvalidateOnRetry}
                noBackgroundOnErrorCard={noBackgroundOnErrorCard}
                showErrorCardOnBackendError={showErrorCardOnBackendError}
                showErrorCard={showErrorCard}
                {...props}
            />
        ),
        [queriesToInvalidateOnRetry, noBackgroundOnErrorCard, showErrorCardOnBackendError, showErrorCard],
    );

    if (fallbackComponent) {
        return (
            <ErrorBoundary onError={logErrorFromErrorBoundary(appNamespace)} FallbackComponent={fallbackComponent}>
                {children}
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary onError={logErrorFromErrorBoundary(appNamespace)} FallbackComponent={FallbackErrorCard}>
            {children}
        </ErrorBoundary>
    );
};

export default ErrorCatcher;
